import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ChevronRight, ArrowUpRight, ArrowDownRight, Activity, GitCompare } from "lucide-react";
import SignalBadge from "./SignalBadge";
import { RsiGauge, MacdBar, EmaTrend } from "./IndicatorBar";
import LanguageSwitcher from "./LanguageSwitcher";
import PerformancePanel from "./PerformancePanel";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PairDetail() {
  const { from, to } = useParams();
  const { t, lang, pn, dir } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/analysis/${from}/${to}`, { params: { lang } })
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [from, to, lang]);

  const localeCode = { ar: "ar-EG", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES", it: "it-IT", ru: "ru-RU", tr: "tr-TR", pt: "pt-BR", zh: "zh-CN", bg: "bg-BG" }[lang] || "en-US";

  if (loading) {
    return (
      <div className="min-h-screen p-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-10 w-48 bg-zinc-900" />
        <Skeleton className="h-80 bg-zinc-900" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-24 bg-zinc-900" />
          <Skeleton className="h-24 bg-zinc-900" />
          <Skeleton className="h-24 bg-zinc-900" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 gap-3">
        {t("no_pair_data")}
        <Link to="/" className="text-emerald-400">{t("back")}</Link>
      </div>
    );
  }

  const { symbol, signal, confidence, indicators, candles, reasoning_ar, updated_at } = data;
  const stroke = signal === "buy" ? "#10B981" : signal === "sell" ? "#EF4444" : "#F59E0B";
  const up = indicators.change_pct >= 0;

  return (
    <div className="min-h-screen grid-bg" data-testid="pair-detail">
      <div className="border-b border-zinc-900 bg-[#0A0A0A]/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-zinc-500 hover:text-white transition-colors" data-testid="back-link">
              {t("back")}
            </Link>
            <ChevronRight className={cn("h-4 w-4 text-zinc-700", dir === "rtl" && "rotate-180")} />
            <span className="ticker text-white">{symbol}</span>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="ticker text-4xl font-bold mb-2" dir="ltr">{symbol}</div>
            <div className="text-zinc-500">{pn(symbol)}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <SignalBadge signal={signal} confidence={confidence} size="lg" />
            <div className="text-[11px] text-zinc-600">
              {t("updated")}: {new Date(updated_at).toLocaleString(localeCode)}
            </div>
          </div>
        </div>

        <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs text-zinc-500 mb-1">{t("current_price")}</div>
              <div className="num text-5xl font-bold" dir="ltr">
                {indicators.price.toFixed(indicators.price > 50 ? 3 : 5)}
              </div>
            </div>
            <div className={cn("flex items-center gap-1 num text-2xl font-bold",
              up ? "text-emerald-400" : "text-red-400"
            )} dir="ltr">
              {up ? <ArrowUpRight className="h-6 w-6" /> : <ArrowDownRight className="h-6 w-6" />}
              {up ? "+" : ""}{indicators.change_pct.toFixed(2)}%
            </div>
          </div>

          <div className="h-72" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={candles}>
                <defs>
                  <linearGradient id="detail-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                <XAxis dataKey="date" stroke="#71717a" fontSize={10} tick={{ fontFamily: 'JetBrains Mono' }} />
                <YAxis domain={['auto', 'auto']} stroke="#71717a" fontSize={10} orientation="right" tick={{ fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono" }}
                  labelStyle={{ color: "#a1a1aa" }}
                  itemStyle={{ color: stroke }}
                />
                <Area type="monotone" dataKey="close" stroke={stroke} strokeWidth={2} fill="url(#detail-grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-6" data-testid="ai-reasoning">
          <div className="flex items-center gap-2 text-emerald-400 text-xs mb-3">
            <Activity className="h-3.5 w-3.5" />
            <span>{t("ai_analysis")}</span>
          </div>
          <p className="text-base leading-relaxed text-zinc-200 whitespace-pre-line">{reasoning_ar}</p>
        </Card>

        <PerformancePanel from={from} to={to} timeframe="5m" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <IndicatorCard title={t("rsi_title")} subtitle="RSI(14)">
            <div className="num text-3xl font-bold mb-3">{indicators.rsi.toFixed(2)}</div>
            <RsiGauge value={indicators.rsi} />
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              {indicators.rsi < 30 ? t("rsi_low") : indicators.rsi > 70 ? t("rsi_high") : t("rsi_mid")}
            </p>
          </IndicatorCard>

          <IndicatorCard title={t("macd_title")} subtitle="12 / 26 / 9">
            <div className="num text-3xl font-bold mb-3" dir="ltr">{indicators.macd.toFixed(5)}</div>
            <MacdBar hist={indicators.macd_hist} />
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              {indicators.macd > indicators.macd_signal ? t("macd_pos") : t("macd_neg")}
            </p>
          </IndicatorCard>

          <IndicatorCard title={t("ema_title")} subtitle="EMA 20 / 50">
            <div className="space-y-1 mb-3">
              <div className="flex justify-between"><span className="text-xs text-zinc-500">EMA 20</span><span className="num font-bold">{indicators.ema_20.toFixed(5)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-zinc-500">EMA 50</span><span className="num font-bold">{indicators.ema_50.toFixed(5)}</span></div>
            </div>
            <EmaTrend price={indicators.price} ema20={indicators.ema_20} ema50={indicators.ema_50} />
          </IndicatorCard>
        </div>

        <div className="pt-6 flex items-center gap-3 flex-wrap">
          <Link to="/">
            <Button variant="outline" className="border-zinc-700 hover:bg-zinc-900 rounded-sm" data-testid="back-button">
              {t("back_button")}
            </Button>
          </Link>
          <Link to={`/compare?pairs=${symbol}&timeframe=5m`}>
            <Button variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 rounded-sm gap-2" data-testid="compare-button">
              <GitCompare className="h-3.5 w-3.5" />
              {t("compare")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function IndicatorCard({ title, subtitle, children }) {
  return (
    <Card className="bg-[#121212] border border-zinc-800 rounded-sm p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-sm font-bold text-white">{title}</h3>
        <span className="num text-[10px] text-zinc-600">{subtitle}</span>
      </div>
      {children}
    </Card>
  );
}
